import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useStudioPageChromeOverride } from "../../common/StudioPageChromeContext";
import { canUseOrders } from "../../helpers/orderEnabled";
import { addSalesMemoLine, getSalesMemo } from "../../services/studioSalesApi";
import { PickerProduct } from "../../components/products/productSelection";
import SalesInventoryPicker from "./SalesInventoryPicker";
import { salesSelectionReason } from "./salesProductSelection";
import "../gi-registry/gi-registry.scss";
import "../orders/orders.scss";

type Failure = { product: PickerProduct; reason: string };
const SalesAddProductsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [memo, setMemo] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [feedback, setFeedback] = useState("");
  const [failures, setFailures] = useState<Failure[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [reload, setReload] = useState(0);
  const lock = useRef(false);
  const role = (() => { try { return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role; } catch { return undefined; } })();
  useStudioPageChromeOverride({ title: "Add Products", subtitle: memo?.memoCode || "Billing Memo", onBack: () => { if (!lock.current) history.push(`/sales/memos/${id}`); } });
  const canWrite = canUseOrders(role) && role !== "SUPER_ADMIN";
  useEffect(() => {
    let active = true;
    if (!canWrite) { history.replace("/sales"); return; }
    setLoadError(false);
    getSalesMemo(id).then((response: any) => {
      if (!active) return;
      if (!response?.success) { setLoadError(true); return; }
      const data = response.data;
      if (data.type !== "SALE" || data.status !== "DRAFT") { history.replace(`/sales/memos/${id}`); return; }
      setMemo(data);
    }).catch(() => { if (active) setLoadError(true); });
    return () => { active = false; };
  }, [canWrite, history, id, reload]);

  const addSelected = async (products: PickerProduct[]) => {
    if (lock.current) return { addedIds: [] };
    lock.current = true; setBusy(true); setFailures([]); setFeedback("");
    const addedIds: string[] = [];
    const failed: Failure[] = [];
    try {
      for (let index = 0; index < products.length; index += 1) {
        const product = products[index];
        setProgress(`Adding ${index + 1} of ${products.length}…`);
        try {
          const response: any = await addSalesMemoLine(id, product.productUniqueId);
          if (response?.success) {
            addedIds.push(product.productUniqueId);
            if (response.data?.lines) setMemo(response.data);
          } else failed.push({ product, reason: salesSelectionReason(response?.message) });
        } catch {
          failed.push({ product, reason: "We couldn't confirm this addition. Refresh the memo before retrying." });
        }
      }
      // Reconcile possible accepted requests whose responses were lost before offering a retry.
      const refreshed: any = await getSalesMemo(id);
      if (!refreshed?.success) throw new Error("Refresh unavailable");
      setMemo(refreshed.data);
      const activeIds = new Set<string>((refreshed.data.lines || []).filter((line: any) => line.lineStatus === "ACTIVE").map((line: any) => line.productUniqueId));
      const remaining = products.filter((product) => !activeIds.has(product.productUniqueId)).map((product) =>
        failed.find((failure) => failure.product.productUniqueId === product.productUniqueId) ||
        { product, reason: "This product is not on the refreshed memo. Review the memo before retrying." });
      for (let index = addedIds.length - 1; index >= 0; index--) {
        if (!activeIds.has(addedIds[index])) addedIds.splice(index, 1);
      }
      products.forEach((product) => { if (activeIds.has(product.productUniqueId) && !addedIds.includes(product.productUniqueId)) addedIds.push(product.productUniqueId); });
      setFailures(remaining);
      if (!remaining.length) history.push(`/sales/memos/${id}`);
      else setFeedback(`${addedIds.length} of ${products.length} products added. The products below were not added; review the reasons or return to the memo.`);
    } catch {
      setFailures(failed);
      setFeedback(`${addedIds.length} additions confirmed. We couldn't refresh the memo. Return to the memo to verify its contents before adding again.`);
      // Uncertain authoritative state: don't allow the same selection to be submitted again.
      setLoadError(true);
    } finally { lock.current = false; setBusy(false); setProgress(""); }
    return { addedIds };
  };
  return <div className="gi-registry-page sales-page sales-add-products">
    <div className="gi-toolbar-row"><h2>Add Products · {memo?.memoCode || "Billing Memo"}</h2></div>
    <p className="gi-au-hint">Search or scan, select products, then add them to this memo.</p>
    {loadError ? <div role="alert"><p>{feedback || "We couldn't load the billing memo. Check your connection and try again."}</p>
      <button type="button" onClick={() => setReload((value) => value + 1)}>Refresh memo</button>{" "}
      <button type="button" onClick={() => history.push(`/sales/memos/${id}`)}>Return to memo</button></div>
      : memo ? <SalesInventoryPicker busy={busy} progress={progress}
        initialExact={new URLSearchParams(location.search).get("add") || ""}
        addedIds={(memo.lines || []).filter((line: any) => line.lineStatus === "ACTIVE").map((line: any) => line.productUniqueId)}
        onAdd={addSelected} onDone={() => history.push(`/sales/memos/${id}`)}
        feedback={<>{feedback && <p role="status">{feedback}</p>}{failures.length > 0 && <div role="alert" className="hkp-product-picker__failures"><ul>{failures.map(({ product, reason }) =>
          <li key={product.productUniqueId}><strong>{product.name || product.productUniqueId}</strong> ({product.productUniqueId}): {reason}</li>)}</ul></div>}</>} />
      : <p role="status">Loading memo…</p>}
  </div>;
};
export default SalesAddProductsPage;
